import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styled/theme';
import { Container, GradientTitle } from '../styled/BaseComponents';
import { getAuctionList } from '../../../../utils/services/user.service';
import AuctionCard from '../../auction/components/AuctionCard';
import { Ring } from '@uiball/loaders';

const AuctionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-top: 1.5rem;

  @media (max-width: ${theme.breakpoints.xl}) {
    grid-template-columns: repeat(4, 1fr);
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const LiveAuctionsWrapper = styled.section`
  margin-top: 4rem;
  padding: 2rem 0;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled(GradientTitle)`
  padding-left: 8px;
  margin: 0;
`;

const ViewStoresButton = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 0.5rem;
  background: linear-gradient(90deg, ${theme.colors.accent}, ${theme.colors.accent2});
  color: #000;
  border: none;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.9;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
`;

const EmptyState = styled.p`
  text-align: center;
  color: ${theme.colors.muted};
  padding: 2rem;
  font-size: 0.875rem;
`;


const LiveAuctions = ({ onAuctionCountChange }) => {
  const history = useHistory();
  const [auctions, setAuctions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [countdowns, setCountdowns] = useState({});
  const [isComingSoon, setIsComingSoon] = useState({});

  useEffect(() => {
    fetchLiveAuctions();
  }, []);

  const fetchLiveAuctions = async () => {
    try {
      setIsLoading(true);
      // First fetch to get total count (with a high limit or without limit)
      const payload = {
        page_number: 1,
        limit: 6, // Fetch a large number to get all auctions for counting
      };
      const response = await getAuctionList(payload);
      console.log("🚀 ~ fetchLiveAuctions ~ response:", response)

      let allAuctions = [];
      if (response.data?.data && Array.isArray(response.data.data)) {
        allAuctions = response.data.data;
      } else if (response.data?.auctions) {
        allAuctions = response.data.auctions;
      } else if (response.data?.result) {
        allAuctions = response.data.result;
      } else if (Array.isArray(response.data)) {
        allAuctions = response.data;
      }

      // Filter to only show live auctions (end time not passed)
      const now = Date.now();
      const liveAuctions = allAuctions.filter(auction => {
        const endTime = auction?.acution_end_time || auction?.auction_end_time;
        if (!endTime) return false;
        const endDate = new Date(endTime).getTime();
        return endDate > now; // Only include if end time is in the future
      });

      // Pass total count to parent component
      if (onAuctionCountChange) {
        onAuctionCountChange(response.data.count || 0);
      }

      setAuctions(liveAuctions.slice(0, 6)); // Limit to 6 auctions for display
    } catch (error) {
      console.error('Error fetching live auctions:', error);
      if (onAuctionCountChange) {
        onAuctionCountChange(0);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate countdowns for auctions
  useEffect(() => {
    if (auctions.length === 0) return;

    const updateCountdowns = () => {
      const nowUTC = Date.now();
      const newCountdowns = {};
      const newIsComingSoon = {};

      auctions.forEach((auction) => {
        const startTime = auction.auction_start_time;
        if (startTime) {
          const startDate = new Date(startTime);
          const startUTC = startDate.getTime();
          if (startUTC > nowUTC) {
            newIsComingSoon[auction._id] = true;
            const diff = startUTC - nowUTC;
            const hours = Math.floor(diff / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            newCountdowns[auction._id] = `Starts in ${hours}h ${minutes}m`;
            return;
          }
        }

        newIsComingSoon[auction._id] = false;

        const endTime = auction.acution_end_time || auction.auction_end_time;
        if (!endTime) {
          newCountdowns[auction._id] = 'Ongoing';
          return;
        }

        const endDate = new Date(endTime);
        const diff = endDate.getTime() - nowUTC;

        if (diff <= 0) {
          newCountdowns[auction._id] = 'ended';
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const seconds = Math.floor((diff % (1000 * 60)) / 1000);

          if (hours > 0) {
            newCountdowns[auction._id] = `${hours}h ${minutes}m ${seconds}s`;
          } else if (minutes > 0) {
            newCountdowns[auction._id] = `${minutes}m ${seconds}s`;
          } else {
            newCountdowns[auction._id] = `${seconds}s`;
          }
        }
      });

      setCountdowns(newCountdowns);
      setIsComingSoon(newIsComingSoon);
    };

    updateCountdowns();
    const interval = setInterval(updateCountdowns, 1000);
    return () => clearInterval(interval);
  }, [auctions]);

  const handleViewAuction = (productId, storeId) => {
    history.push(`/sign-in`);
  };

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isLoggedIn = user && user.token;
  const handleViewStores = () => {

    if (isLoggedIn) {
      history.push('/store');
    } else {
      history.push('/sign-in');
    }
  };

  if (isLoading) {
    return (
      <LiveAuctionsWrapper>
        <Container>
          <SectionTitle>Live Auctions</SectionTitle>
          <LoadingContainer>
            <Ring size={40} color="#5ce9ff" />
          </LoadingContainer>
        </Container>
      </LiveAuctionsWrapper>
    );
  }

  if (auctions.length === 0) {
    return null; // Don't show section if no live auctions
  }

  return (
    <LiveAuctionsWrapper>
      <Container>
        <SectionHeader>
          <SectionTitle>Live Auctions</SectionTitle>
          {isLoggedIn && <ViewStoresButton onClick={handleViewStores}>
            View All Stores
          </ViewStoresButton>}
        </SectionHeader>
        <AuctionsGrid>
          {auctions.map((auction) => (
            <AuctionCard
              key={auction._id}
              auction={auction}
              countdown={countdowns[auction._id]}
              isComingSoon={isComingSoon[auction._id]}
              homepageStyle={true}
              onClick={() => handleViewAuction(
                auction?.product_info?.[0]?._id || auction?.product_id,
                auction.store_id
              )}
            />
          ))}
        </AuctionsGrid>
      </Container>
    </LiveAuctionsWrapper>
  );
};

export default LiveAuctions;

